import React, { useState, useEffect, useCallback, useRef } from "react";
import { FiMail, FiChevronDown, FiChevronUp, FiCornerUpLeft, FiCornerUpRight, FiArchive, FiTrash2, FiFolderPlus, FiFileText } from "react-icons/fi";
import api from "../api";
import EmailReplyModal from "../components/utils/EmailReplyModal";
import "./Email.css";

const Email = ({ participantEmail, participantName, showToast, currentUser }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [replyEmail, setReplyEmail] = useState(null);
  const [replyAllMode, setReplyAllMode] = useState(false);
  const [gmailLabels, setGmailLabels] = useState([]);
  const [showLabelMenu, setShowLabelMenu] = useState(null);
  
  // Compose form state
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [variableValues, setVariableValues] = useState({});
  const bodyEditorRef = useRef(null);

  const cleanText = (text) => {
    if (!text) return "";
    return text.replace(/Â/g, "");
  };

  const getEmailAddress = (headerText) => {
    if (!headerText) return "";
    const match = headerText.match(/<([^>]+)>/);
    return match ? match[1].trim() : headerText.trim();
  };

  const parseEmails = (header) => {
    if (!header) return [];
    return header.split(",").map((addr) => getEmailAddress(addr));
  };

  // Fetch email templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.get("/email-templates");
        setTemplates(response.data || []);
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    fetchTemplates();
  }, []);

  // Fetch Gmail labels
  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const response = await api.get("/google/gmail/labels");
        setGmailLabels(response.data || []);
      } catch (error) {
        console.error("Error fetching labels:", error);
      }
    };
    fetchLabels();
  }, []);

  // Initialize compose form when opening
  useEffect(() => {
    if (showCompose && participantEmail) {
      setTo(participantEmail);
      setSubject("");
      setBody("");
      setCc("");
      setBcc("");
      setShowCcBcc(false);
      setVariableValues({});
    }
  }, [showCompose, participantEmail]);

  const fetchEmails = useCallback(async () => {
    console.log("🔍 [fetchEmails] Starting for:", participantEmail);
    
    if (!participantEmail) {
      console.log("🔍 [fetchEmails] No participant email provided, skipping");
      setEmails([]);
      setLoading(false);
      return;
    }

    console.log("🔍 [fetchEmails] Setting loading to true");
    setLoading(true);
    
    try {
      console.log("🔍 [fetchEmails] Making API call to /google/gmail/messages");
      // Pass participantEmail to backend for server-side filtering
      const res = await api.get("/google/gmail/messages", {
        params: { participantEmail }
      });
      
      console.log(`🔍 [fetchEmails] Fetched ${res.data.length} emails for ${participantEmail}`);
      
      const processedEmails = await Promise.all(
        res.data.map(async (email) => {
          let bodyText = "";
          if (email.payload.body && email.payload.body.data) {
            bodyText = atob(
              email.payload.body.data.replace(/-/g, "+").replace(/_/g, "/")
            );
          } else if (email.payload.parts) {
            const htmlPart = email.payload.parts.find(
              (part) => part.mimeType === "text/html"
            );
            if (htmlPart && htmlPart.body && htmlPart.body.data) {
              bodyText = atob(
                htmlPart.body.data.replace(/-/g, "+").replace(/_/g, "/")
              );
            }
          }
          return { ...email, fullBody: cleanText(bodyText) };
        })
      );

      // Backend now filters by from/to/cc, but we can keep additional frontend filtering
      // for emails that mention the participant in the body
      const lowerParticipant = participantEmail.toLowerCase();
      const filtered = processedEmails.filter((email) => {
        const headers = email.payload?.headers || [];
        const getHeader = (name) =>
          headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value || "";
        const fromRaw = getHeader("From");
        const toRaw = getHeader("To");
        const ccRaw = getHeader("Cc");

        const fromEmails = parseEmails(fromRaw);
        const toEmails = parseEmails(toRaw);
        const ccEmails = parseEmails(ccRaw);

        const matchInHeaders =
          fromEmails.some((e) => e.toLowerCase().includes(lowerParticipant)) ||
          toEmails.some((e) => e.toLowerCase().includes(lowerParticipant)) ||
          ccEmails.some((e) => e.toLowerCase().includes(lowerParticipant));

        const matchInBody = email.fullBody?.toLowerCase().includes(lowerParticipant);

        return matchInHeaders || matchInBody;
      });

      console.log(`🔍 [fetchEmails] ${filtered.length} emails match the participant after filtering`);
      setEmails(filtered);
    } catch (err) {
      console.error("🔍 [fetchEmails] Error fetching emails:", err);
      console.error("🔍 [fetchEmails] Error details:", err.response?.data || err.message);
      setEmails([]);
    } finally {
      console.log("🔍 [fetchEmails] Setting loading to false");
      setLoading(false);
    }
  }, [participantEmail]);

  useEffect(() => {
    console.log("📨 Received participantEmail in Email component:", participantEmail);
    console.log("📨 Loading state:", loading);
    console.log("📨 Emails count:", emails.length);
    if (participantEmail) {
      console.log("📨 Calling fetchEmails...");
      fetchEmails();
    }
  }, [participantEmail, fetchEmails]);

  const getHeader = (headers, name) => {
    return headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value || "";
  };

  const handleEmailClick = async (id) => {
    try {
      const res = await api.get(`/google/gmail/message/${id}`);
      setSelectedEmail(res.data);
    } catch (err) {
      console.error("Error fetching email detail:", err);
    }
  };

  const inboxEmails = emails.filter(
    (email) => email.labelIds && email.labelIds.includes("INBOX")
  );
  const sentEmails = emails.filter(
    (email) => email.labelIds && email.labelIds.includes("SENT")
  );

  const hasMultipleRecipients = (email) => {
    const headers = email.payload?.headers || [];
    const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || "";
    const toHeader = getHeader("To");
    const ccHeader = getHeader("Cc");
    const toEmails = parseEmails(toHeader);
    const ccEmails = ccHeader ? parseEmails(ccHeader) : [];
    return (toEmails.length + ccEmails.length) > 1;
  };

  const renderEmailGroup = (title, emailGroup) => (
    <div className="email-section">
      <h3>{cleanText(title)}</h3>
      <div className="email-container">
        {emailGroup.map((email) => {
          const headers = email.payload.headers || [];
          const from = cleanText(getEmailAddress(getHeader(headers, "From")));
          const subject = cleanText(getHeader(headers, "Subject") || "(No Subject)");
          const snippet = cleanText(email.snippet || "");
          const date = new Date(Number(email.internalDate)).toLocaleDateString();
          const showReplyAll = hasMultipleRecipients(email);

          return (
            <div 
              key={email.id} 
              className="email-item-wrapper"
              onClick={() => handleEmailClick(email.id)}
            >
              <div className="email-item">
                <div className="email-item-left">
                  <div className="email-from">{from}</div>
                  <div className="email-subject">{subject}</div>
                  <div className="email-snippet">{snippet}</div>
                </div>
                <div className="email-date">{date}</div>
              </div>
              <div className="email-item-actions" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReply(email);
                  }}
                  title="Reply"
                  className="email-item-action-btn"
                >
                  <FiCornerUpLeft size={16} />
                </button>
                {showReplyAll && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReplyAll(email);
                    }}
                    title="Reply All"
                    className="email-item-action-btn"
                  >
                    <FiCornerUpRight size={16} />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchive(email.id);
                  }}
                  title="Archive"
                  className="email-item-action-btn"
                >
                  <FiArchive size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLabelMenu(showLabelMenu === email.id ? null : email.id);
                  }}
                  title="Move to folder"
                  className="email-item-action-btn"
                >
                  <FiFolderPlus size={16} />
                </button>
                {showLabelMenu === email.id && (
                  <div className="label-menu">
                    {gmailLabels.map(label => (
                      <button
                        key={label.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveToLabel(email.id, label.id);
                        }}
                        className="label-menu-item"
                      >
                        {label.name}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(email.id);
                  }}
                  title="Delete"
                  className="email-item-action-btn email-item-action-delete"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Compose email handlers
  const handleSendCompose = async () => {
    if (!to || !subject || !body) {
      showToast?.("Please fill in all required fields", "error");
      return;
    }

    setSending(true);
    try {
      const finalSubject = getFinalText(subject);
      const finalBody = getFinalText(body);
      
      await api.post("/google/gmail/send", {
        to,
        cc: cc || undefined,
        bcc: bcc || undefined,
        subject: finalSubject,
        body: finalBody.replace(/\n/g, "<br>"),
      });
      
      showToast?.("Email sent successfully", "success");
      setShowCompose(false);
      setTo("");
      setSubject("");
      setBody("");
      setCc("");
      setBcc("");
      await fetchEmails();
    } catch (error) {
      console.error("Error sending email:", error);
      showToast?.("Failed to send email", "error");
    } finally {
      setSending(false);
    }
  };

  const handleSendEmail = async (emailData) => {
    try {
      await api.post("/google/gmail/send", emailData);
      await fetchEmails();
    } catch (error) {
      console.error("Error sending email:", error);
      throw error;
    }
  };

  // Email action handlers
  const handleReply = (email) => {
    setReplyEmail(email);
    setReplyAllMode(false);
  };

  const handleReplyAll = (email) => {
    setReplyEmail(email);
    setReplyAllMode(true);
  };

  const handleArchive = async (emailId) => {
    try {
      await api.post(`/google/gmail/archive/${emailId}`);
      showToast?.("Email archived", "success");
      await fetchEmails();
    } catch (error) {
      console.error("Error archiving email:", error);
      showToast?.("Failed to archive email", "error");
    }
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm("Are you sure you want to delete this email?")) {
      return;
    }
    try {
      await api.delete(`/google/gmail/message/${emailId}`);
      showToast?.("Email deleted", "success");
      await fetchEmails();
    } catch (error) {
      console.error("Error deleting email:", error);
      showToast?.("Failed to delete email", "error");
    }
  };

  const handleMoveToLabel = async (emailId, labelId) => {
    try {
      await api.post(`/google/gmail/move/${emailId}`, { labelId });
      showToast?.("Email moved", "success");
      setShowLabelMenu(null);
      await fetchEmails();
    } catch (error) {
      console.error("Error moving email:", error);
      showToast?.("Failed to move email", "error");
    }
  };

  // Template variable functions
  const getDefaultVariableValue = (variableName) => {
    const varLower = variableName.toLowerCase();
    
    if (varLower === 'name') return participantName || '';
    if (varLower === 'firstname') return participantName?.split(' ')[0] || '';
    if (varLower === 'lastname') return participantName?.split(' ').slice(1).join(' ') || '';
    if (varLower === 'email') return participantEmail || '';
    
    if (currentUser) {
      if (varLower === 'myname') return currentUser.name || '';
      if (varLower === 'myemail') return currentUser.email || '';
      if (varLower === 'mycompany') return currentUser.company || '';
    }
    
    return '';
  };

  const detectVariables = (text) => {
    if (!text) return [];
    const variableRegex = /\{\{(\w+)\}\}/gi;
    const matches = [...text.matchAll(variableRegex)];
    return [...new Set(matches.map(match => match[1].toLowerCase()))];
  };

  const detectedVars = [...new Set([
    ...detectVariables(subject),
    ...detectVariables(body)
  ])];

  useEffect(() => {
    if (!showCompose) return; // Only run when compose is open
    
    const newVars = {};
    detectedVars.forEach(varName => {
      if (!(varName in variableValues)) {
        newVars[varName] = getDefaultVariableValue(varName);
      }
    });
    
    if (Object.keys(newVars).length > 0) {
      setVariableValues(prev => ({ ...prev, ...newVars }));
    }
  }, [detectedVars.join(','), showCompose]);

  const handleBodyEditorInput = (e) => {
    const element = e.currentTarget;
    const parseContent = (node) => {
      let text = '';
      node.childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
          text += child.textContent;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.classList && child.classList.contains('editor-chip')) {
            const varName = child.getAttribute('data-variable');
            text += `{{${varName}}}`;
          } else if (child.tagName === 'BR') {
            text += '\n';
          } else {
            text += parseContent(child);
          }
        }
      });
      return text;
    };
    
    const parsedText = parseContent(element);
    setBody(parsedText);
  };

  useEffect(() => {
    if (bodyEditorRef.current && body && showCompose) {
      const rendered = renderBodyWithChips(body);
      if (bodyEditorRef.current.innerHTML !== rendered) {
        bodyEditorRef.current.innerHTML = rendered;
      }
    }
  }, [showCompose, body]);

  useEffect(() => {
    if (bodyEditorRef.current && showCompose) {
      const rendered = renderBodyWithChips(body);
      if (document.activeElement !== bodyEditorRef.current) {
        if (bodyEditorRef.current.innerHTML !== rendered) {
          bodyEditorRef.current.innerHTML = rendered;
        }
      }
    }
  }, [variableValues, showCompose]);

  const renderBodyWithChips = (text) => {
    if (!text) return '';
    
    const variableRegex = /\{\{(\w+)\}\}/gi;
    let result = text;
    let match;
    const replacements = [];
    
    while ((match = variableRegex.exec(text)) !== null) {
      const varName = match[1].toLowerCase();
      const value = variableValues[varName] || '';
      const chipClass = value ? 'has-value' : 'no-value';
      const chipHtml = `<span class="editor-chip ${chipClass}" contenteditable="false" data-variable="${varName}"><span class="chip-var">${match[1]}</span><span class="chip-separator">:</span><span class="chip-value">${value || '?'}</span></span>`;
      
      replacements.push({
        index: match.index,
        length: match[0].length,
        html: chipHtml
      });
    }
    
    replacements.reverse().forEach(repl => {
      result = result.substring(0, repl.index) + repl.html + result.substring(repl.index + repl.length);
    });
    
    result = result.replace(/\n/g, '<br>');
    return result;
  };

  const getFinalText = (text) => {
    if (!text) return '';
    let result = text;
    Object.entries(variableValues).forEach(([varName, value]) => {
      const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'gi');
      result = result.replace(regex, value);
    });
    return result;
  };

  const handleUseTemplate = (template) => {
    setSubject(template.subject);
    setBody(template.body);
    setShowTemplates(false);
    showToast?.(`Template "${template.name}" applied`, "info");
  };

  const renderEmailDetail = () => {
    if (!selectedEmail) return null;
    const headers = selectedEmail.payload.headers || [];
    const subject = cleanText(getHeader(headers, "Subject"));
    const from = cleanText(getEmailAddress(getHeader(headers, "From")));
    const to = parseEmails(getHeader(headers, "To")).join(", ");
    const cc = parseEmails(getHeader(headers, "Cc")).join(", ");
    const date = new Date(Number(selectedEmail.internalDate)).toLocaleString();
    const showReplyAll = hasMultipleRecipients(selectedEmail);

    let htmlBody = "<p>No content available.</p>";
    if (selectedEmail.payload.parts) {
      const htmlPart = selectedEmail.payload.parts.find(
        (part) => part.mimeType === "text/html"
      );
      if (htmlPart && htmlPart.body && htmlPart.body.data) {
        htmlBody = cleanText(
          atob(htmlPart.body.data.replace(/-/g, "+").replace(/_/g, "/"))
        );
      }
    } else if (selectedEmail.payload.body && selectedEmail.payload.body.data) {
      htmlBody = cleanText(
        atob(selectedEmail.payload.body.data.replace(/-/g, "+").replace(/_/g, "/"))
      );
    }

    return (
      <div className="email-detail">
        <div className="email-detail-header">
          <button className="back-btn" onClick={() => setSelectedEmail(null)}>
            ← Back to Inbox
          </button>
          <div className="email-detail-actions">
            <button
              onClick={() => handleReply(selectedEmail)}
              title="Reply"
              className="email-detail-action-btn"
            >
              <FiCornerUpLeft size={18} />
              Reply
            </button>
            {showReplyAll && (
              <button
                onClick={() => handleReplyAll(selectedEmail)}
                title="Reply All"
                className="email-detail-action-btn"
              >
                <FiCornerUpRight size={18} />
                Reply All
              </button>
            )}
            <button
              onClick={() => {
                handleArchive(selectedEmail.id);
                setSelectedEmail(null);
              }}
              title="Archive"
              className="email-detail-action-btn"
            >
              <FiArchive size={18} />
              Archive
            </button>
            <button
              onClick={() => setShowLabelMenu(showLabelMenu === 'detail' ? null : 'detail')}
              title="Move to folder"
              className="email-detail-action-btn"
              style={{ position: 'relative' }}
            >
              <FiFolderPlus size={18} />
              Move
              {showLabelMenu === 'detail' && (
                <div className="label-menu" style={{ top: '100%', right: 0 }}>
                  {gmailLabels.map(label => (
                    <button
                      key={label.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveToLabel(selectedEmail.id, label.id);
                        setSelectedEmail(null);
                      }}
                      className="label-menu-item"
                    >
                      {label.name}
                    </button>
                  ))}
                </div>
              )}
            </button>
            <button
              onClick={() => {
                handleDelete(selectedEmail.id);
                setSelectedEmail(null);
              }}
              title="Delete"
              className="email-detail-action-btn email-detail-action-delete"
            >
              <FiTrash2 size={18} />
              Delete
            </button>
          </div>
        </div>
        <h2>{subject}</h2>
        <div className="email-meta">
          <div>
            <strong>From:</strong> {from}
          </div>
          <div>
            <strong>To:</strong> {cleanText(to)}
          </div>
          {cc && (
            <div>
              <strong>Cc:</strong> {cleanText(cc)}
            </div>
          )}
          <div>
            <strong>Date:</strong> {date}
          </div>
        </div>
        <div
          className="email-body"
          dangerouslySetInnerHTML={{ __html: htmlBody }}
        />
      </div>
    );
  };

  return (
    <div className="email-tab">
      {participantEmail && !selectedEmail && (
        <div className="compose-section">
          <button 
            className="compose-toggle-button"
            onClick={() => setShowCompose(!showCompose)}
          >
            <FiMail size={16} />
            {showCompose ? 'Hide Compose' : 'Compose Email'}
            {showCompose ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </button>

          {showCompose && (
            <div className="compose-form">
              <div className="email-field">
                <label>To: *</label>
                <input
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="recipient@example.com"
                  disabled={sending}
                />
              </div>

              {!showCcBcc && (
                <button 
                  className="show-cc-bcc-button"
                  onClick={() => setShowCcBcc(true)}
                  type="button"
                >
                  <FiChevronDown size={14} /> Cc / Bcc
                </button>
              )}

              {showCcBcc && (
                <>
                  <div className="email-field">
                    <label>
                      Cc:
                      <button 
                        className="hide-field-button"
                        onClick={() => {
                          setCc("");
                          if (!bcc) setShowCcBcc(false);
                        }}
                        type="button"
                      >
                        <FiChevronUp size={12} />
                      </button>
                    </label>
                    <input
                      type="email"
                      value={cc}
                      onChange={(e) => setCc(e.target.value)}
                      placeholder="cc@example.com"
                      disabled={sending}
                    />
                  </div>

                  <div className="email-field">
                    <label>
                      Bcc:
                      <button 
                        className="hide-field-button"
                        onClick={() => {
                          setBcc("");
                          if (!cc) setShowCcBcc(false);
                        }}
                        type="button"
                      >
                        <FiChevronUp size={12} />
                      </button>
                    </label>
                    <input
                      type="email"
                      value={bcc}
                      onChange={(e) => setBcc(e.target.value)}
                      placeholder="bcc@example.com"
                      disabled={sending}
                    />
                  </div>
                </>
              )}

              <div className="email-field">
                <label>Subject: *</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject"
                  disabled={sending}
                />
              </div>

              {templates.length > 0 && (
                <div className="template-selector">
                  <button 
                    className="template-toggle-button"
                    onClick={() => setShowTemplates(!showTemplates)}
                    type="button"
                  >
                    <FiFileText size={14} /> {showTemplates ? "Hide" : "Use"} Template
                  </button>
                  
                  {showTemplates && (
                    <div className="template-dropdown">
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          className="template-option"
                          onClick={() => handleUseTemplate(template)}
                          type="button"
                        >
                          <strong>{template.name}</strong>
                          <span className="template-preview">{template.subject}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {detectedVars.length > 0 && (
                <div className="variables-section">
                  <div className="variables-header">
                    <label>Template Variables</label>
                    <span className="variables-count">{detectedVars.length} variable{detectedVars.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="variables-form">
                    {detectedVars.map((varName, idx) => (
                      <div key={idx} className="variable-field">
                        <label>
                          <code>{`{{${varName}}}`}</code>
                        </label>
                        <input
                          type="text"
                          value={variableValues[varName] || ''}
                          onChange={(e) => setVariableValues(prev => ({ ...prev, [varName]: e.target.value }))}
                          placeholder={`Enter value for ${varName}...`}
                          disabled={sending}
                          className="variable-input"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="email-field">
                <label>Message: *</label>
                <div
                  ref={bodyEditorRef}
                  className="body-editor"
                  contentEditable={!sending}
                  onInput={handleBodyEditorInput}
                  suppressContentEditableWarning={true}
                  data-placeholder="Type your message..."
                />
              </div>

              <div className="compose-actions">
                <button 
                  className="cancel-button" 
                  onClick={() => setShowCompose(false)} 
                  disabled={sending}
                >
                  Cancel
                </button>
                <button 
                  className="send-button" 
                  onClick={handleSendCompose} 
                  disabled={sending}
                >
                  {sending ? "Sending..." : "Send Email"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {loading ? (
        <p>Loading emails...</p>
      ) : !participantEmail ? (
        <p style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
          No email address available for this contact.
        </p>
      ) : selectedEmail ? (
        renderEmailDetail()
      ) : emails.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
          No emails found for {participantEmail}.
        </p>
      ) : (
        <>
          {renderEmailGroup("Inbox", inboxEmails)}
          {renderEmailGroup("Sent", sentEmails)}
        </>
      )}

      {/* Email Reply Modal */}
      {replyEmail && (
        <EmailReplyModal
          email={replyEmail}
          replyAll={replyAllMode}
          person={{ name: participantName, email: participantEmail }}
          currentUser={currentUser}
          onClose={() => {
            setReplyEmail(null);
            setReplyAllMode(false);
          }}
          onSend={handleSendEmail}
          showToast={showToast}
        />
      )}
    </div>
  );
};

export default Email;
